import React, { useState } from 'react';

interface LeadFormField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'number' | 'date' | 'datetime' | 'url' | 'enum';
  required: boolean;
  options?: Array<{ label: string; value: string }>;
  value?: unknown;
}

interface Props {
  /** The `lead-capture-form` metadata sent on the SSE `complete` event — see
   *  LeadCaptureEngine.buildFormSpec on the backend for the exact shape. The intro line shown here
   *  is fixed/built-in (not the agent's configured Intro Message — that's a sequential-mode-only
   *  conversational opener). `fieldsByService` (present only with 2+ configured services) holds
   *  each service's own extra fields, keyed by service id — spliced into the visible field list the
   *  moment the visitor picks that service from the `__service` dropdown, so Program/etc. appears in
   *  the SAME form instead of needing a second round trip to the backend to learn what to ask. */
  metadata: {
    fields?: LeadFormField[];
    fieldsByService?: Record<string, LeadFormField[]>;
  };
  disabled?: boolean;
  onSubmit: (values: Record<string, string>) => void;
  /** The chatbot's configured theme (PublicChatWidget's own `theme` state) — same tokens the rest of
   *  the widget already reads, so the form/modal match the chatbot's brand colors instead of a fixed
   *  palette. Every token is optional; falls back to the same defaults the widget itself uses when a
   *  chatbot hasn't customized that particular color. */
  theme?: {
    botMsgBg?: string;
    botMsgFont?: string;
    sendBtn?: string;
    inputBorder?: string;
    inputTextBg?: string;
    inputTextFont?: string;
  };
}

/** Native `<input>` type for a field type that isn't `enum` (handled separately as a `<select>`). */
function inputTypeFor(type: LeadFormField['type']): string {
  switch (type) {
    case 'email': return 'email';
    case 'phone': return 'tel';
    case 'number': return 'number';
    case 'date': return 'date';
    case 'datetime': return 'datetime-local';
    case 'url': return 'url';
    default: return 'text';
  }
}

/**
 * Chat-widget counterpart to SymptomCheckerAssessmentMessageBubble: the lead-capture turn's reply is
 * a short line + a "Click to open" link, and the actual form (one input per field, all at once)
 * opens in an overlay above the chat only once clicked — never inline by default, so the chat scroll
 * stays short even when the configured field list is long. Pure client-side form — the backend
 * (LeadCaptureNodeExecutor.runFormTurn) owns validation/matching on submit; this component only
 * enforces `required` so an obviously-incomplete submit doesn't round-trip. Submit button/link/labels
 * follow the chatbot's own theme (see `theme` prop); the modal card itself stays a neutral white
 * regardless, so input contrast stays predictable even for a dark-themed chatbot.
 */
export const LeadCaptureFormBubble: React.FC<Props> = ({ metadata, disabled, onSubmit, theme }) => {
  const baseFields = metadata?.fields ?? [];
  const fieldsByService = metadata?.fieldsByService;
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const f of baseFields) {
      if (f.value !== undefined && f.value !== null) initial[f.key] = String(f.value);
    }
    return initial;
  });
  const [touched, setTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [open, setOpen] = useState(false);

  // The visible field list reacts to the __service picker (when one is offered): once a service is
  // chosen, that service's own fields (e.g. Program) are appended right after the base fields, in
  // this same form — see fieldsByService's doc comment above for why.
  const selectedServiceId = values['__service'];
  const serviceFields = (selectedServiceId && fieldsByService?.[selectedServiceId]) || [];
  const fields = [...baseFields, ...serviceFields];

  const update = (key: string, v: string) => {
    setValues((prev) => {
      const next = { ...prev, [key]: v };
      // Changing the service clears any other service's now-irrelevant fields, so a stale value
      // from a previously-picked service never gets silently submitted alongside the new one.
      if (key === '__service' && fieldsByService) {
        for (const [sid, sFields] of Object.entries(fieldsByService)) {
          if (sid === v) continue;
          for (const f of sFields) delete next[f.key];
        }
      }
      return next;
    });
  };

  const missing = fields.filter((f) => f.required && !values[f.key]?.trim());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (missing.length > 0 || disabled || submitted) return;
    setSubmitted(true);
    setOpen(false);
    onSubmit(values);
  };

  if (!fields.length) return null;

  return (
    <div className="space-y-2">
      <p className="text-sm">
        Before we proceed further, I would like to get some details. Would you be kind enough to
        provide the required details.
      </p>
      {submitted ? (
        <p className="text-sm text-gray-500 italic">Details submitted.</p>
      ) : (
        <p className="text-sm">
          A secure form has been sent to you:{' '}
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen(true)}
            className="underline font-medium disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-80"
            style={{ color: theme?.sendBtn || '#2563eb' }}
          >
            Click to open
          </button>
        </p>
      )}

      {open && !submitted && (
        // `absolute`, not `fixed`: contained within the chat widget's own box (chatShell in
        // PublicChatWidget.tsx, given `relative` for exactly this) instead of covering the whole
        // page — a public widget can be embedded alongside other page content that must stay clear.
        <div
          className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-sm max-h-[85%] overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold" style={{ color: theme?.botMsgFont || '#1f2937' }}>Please provide your details</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              {fields.map((f) => {
                const isMissing = touched && f.required && !values[f.key]?.trim();
                return (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {f.label}
                      {f.required && <span className="text-red-500"> *</span>}
                    </label>
                    {f.type === 'enum' ? (
                      <select
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        style={{
                          background: theme?.inputTextBg || '#ffffff',
                          color: theme?.inputTextFont || '#111827',
                          borderColor: isMissing ? '#f87171' : theme?.inputBorder || '#d1d5db',
                        }}
                        value={values[f.key] ?? ''}
                        disabled={disabled || submitted}
                        onChange={(e) => update(f.key, e.target.value)}
                      >
                        <option value="">Select an option</option>
                        {(f.options ?? []).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={inputTypeFor(f.type)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        style={{
                          background: theme?.inputTextBg || '#ffffff',
                          color: theme?.inputTextFont || '#111827',
                          borderColor: isMissing ? '#f87171' : theme?.inputBorder || '#d1d5db',
                        }}
                        value={values[f.key] ?? ''}
                        disabled={disabled || submitted}
                        onChange={(e) => update(f.key, e.target.value)}
                      />
                    )}
                    {isMissing && <p className="text-xs text-red-500 mt-0.5">Required</p>}
                  </div>
                );
              })}
              <button
                type="submit"
                disabled={disabled || submitted}
                className="w-full px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 disabled:cursor-not-allowed transition hover:opacity-90"
                style={{ background: theme?.sendBtn || '#2563eb' }}
              >
                {submitted ? 'Submitted' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
